import { addLines, getOrCreateCart, removeLine, setCartAttributes, setCartNote, updateLineQuantity } from './cart';

const createStore = (Alpine) => {
    Alpine.store('statamic.shopify.cart', {
        cartId: null,
        checkoutUrl: '',
        lineItems: [],
        market: window.shopifyConfig?.market,
        note: '',
        subtotal: null,
        // true zodra bekend is wat er in de winkelwagen zit (ook als die leeg is).
        ready: false,

        async init() {
            this.cartId = localStorage.getItem('statamic.shopify.cart.id');

            if (this.cartId) {
                try {
                    this.updateFromResponse(await getOrCreateCart(this.cartId));
                } catch (e) {
                    // Geen verbinding met Shopify: de pagina blijft bruikbaar, de winkelwagen toont zich leeg.
                }
            }

            this.ready = true;

            window.addEventListener('shopify.updateMarket', (event) => {
                this.market = event.detail.market;
            });
        },

        // Zoals in de winkel: hele euro's zonder decimalen (€ 1.299), anders € 229,50.
        formatCurrency(amount) {
            const currency = (window.shopifyConfig && window.shopifyConfig.currency) || '€';
            const bedrag = parseFloat(amount) || 0;
            const heel = Number.isInteger(bedrag);

            return currency + '\u00a0' + bedrag.toLocaleString('nl-NL', { minimumFractionDigits: heel ? 0 : 2, maximumFractionDigits: heel ? 0 : 2 });
        },

        getItems() {
            return this.lineItems;
        },

        itemCount() {
            return this.lineItems.reduce((total, item) => total + Number(item.qty || 0), 0);
        },

        hasItems() {
            return this.itemCount() > 0;
        },

        removeLine(id) {
            removeLine(this.cartId, id).then(response => this.updateFromResponse(response));
        },

        setAttributes(attrs) {
            setCartAttributes(this.cartId, attrs).then(response => this.updateFromResponse(response));
        },

        setNote(note) {
            setCartNote(this.cartId, note).then(response => this.updateFromResponse(response));
        },

        updateQuantity(id, quantity) {
            updateLineQuantity(this.cartId, id, quantity).then(response => this.updateFromResponse(response));
        },

        updateFromResponse(checkout) {
            if (!checkout || !checkout.lines || !checkout.lines.edges) {
                return;
            }

            let mappedLineItems = [];
            for (let line of checkout.lines.edges) {
                line = line.node;

                let attrs = {};
                line.attributes.forEach((attr) => attrs[attr.key] = attr.value);

                let qty = line.quantity;

                mappedLineItems.push({
                    id: line.id,
                    title: line.merchandise.product.title,
                    // Het nummer van het product in Shopify: daarmee vindt de winkelwagen de naam van de site.
                    productId: String(line.merchandise.product.id || '').split('/').pop(),
                    variant: {
                        // Shopify noemt de enige variant van een product "Default Title".
                        title: line.merchandise.title === 'Default Title' ? '' : line.merchandise.title,
                    },
                    price:this.formatCurrency(line.cost.amountPerQuantity.amount),
                    qty: qty,
                    subtotal: this.formatCurrency(line.cost.amountPerQuantity.amount * qty),
                    image: line.merchandise?.image?.url,
                    attributes: attrs,
                });
            }

            this.cartId = checkout.id;
            this.lineItems = mappedLineItems;
            this.note = checkout.note;
            this.subtotal = this.formatCurrency(checkout.cost.totalAmount.amount);
            this.checkoutUrl = checkout.checkoutUrl;

            localStorage.setItem('statamic.shopify.cart.id', this.cartId);
        }
    });
}

const createData = (Alpine) => {
    Alpine.data('shopifyProduct', (options, variants) => ({
        added: false,
        adding: false,
        customAttributes: {},
        error: '',
        options: options,
        selected: {},
        selectedVariant: false,
        variants: variants,
        variantEl: null,
        qty: 1,

        init() {
            this.variantEl = this.$el.querySelector('[name="ss-product-variant"]');

            if (! variants.length) {
                return;
            }

            if (! options) {
                options = {};
            }

            this.selectedVariant = variants[0];
            this.variantEl.value = this.selectedVariant.slug;

            for (const [key, value] of Object.entries(options)) {
                this.selected[key] = this.selectedVariant[key];
            }
        },

        allOptionsSelected() {
            let selectedValues = [];

            for (const [key, value] of Object.entries(options)) {
                if (this.selected[key] ?? false) {
                    selectedValues.push(this.selected[key]);
                }
            }

            return selectedValues.length == Object.values(options).length;
        },

        getOptions(index, value) {
            let vals = [];
            Object.values(variants).forEach((opt) => {
                vals.push(opt[index]);
            });

            return [... new Set(vals)];
        },

        async handleSubmit(target) {
            this.error = '';

            let variantId = this.variantEl?.value;

            if (! variantId) {
                this.error = 'Kies eerst een geldige variant.';
                return;
            }

            let attributes = [];
            for (const key in this.customAttributes) {
                attributes.push({key: key, value: this.customAttributes[key] + '' });
            }

            let qty = Math.max(1, parseInt(this.qty) || 1);

            if (! this.heeftPrijs()) {
                this.error = 'Voor deze uitvoering is online geen prijs bekend. Neem contact met ons op.';
                return;
            }

            this.adding = true;
            let cart = await getOrCreateCart(Alpine.store('statamic.shopify.cart').cartId);

            if (! cart?.id) {
                this.adding = false;
                this.error = 'De winkelwagen kon niet worden aangemaakt. Controleer de Shopify Storefront token.';
                return;
            }

            let response = await addLines(cart.id, [{
                attributes: attributes,
                merchandiseId: "gid://shopify/ProductVariant/" + variantId,
                quantity: qty,
            }]);

            if (response) {
                Alpine.store('statamic.shopify.cart').updateFromResponse(response);
            } else {
                this.adding = false;
                this.error = 'Dit product kon niet worden toegevoegd. Controleer de Shopify-koppeling of probeer het opnieuw.';
                return;
            }

            this.adding = false;
            this.added = true;

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: 'shop_in_winkelwagen', product: this.selectedVariant?.product_slug, aantal: qty });
        },

        // De bevestiging verdwijnt zodra de bezoeker iets anders kiest.
        minder() {
            this.qty = Math.max(1, (parseInt(this.qty) || 1) - 1);
            this.added = false;
        },

        meer() {
            this.qty = Math.min(99, (parseInt(this.qty) || 1) + 1);
            this.added = false;
        },

        heeftPrijs() {
            return parseFloat(this.getVariantPrice(this.selectedVariant || {}) || 0) > 0;
        },

        // Te bestellen: de variant bestaat, mag verkocht worden en heeft een prijs.
        kanBestellen() {
            return this.variantExistsAndIsInStock() && this.heeftPrijs();
        },

        prijs() {
            const bedrag = parseFloat(this.getVariantPrice(this.selectedVariant || {}) || 0);

            if (! (bedrag > 0)) {
                return '';
            }

            const heel = Number.isInteger(bedrag);

            return '€\u00a0' + bedrag.toLocaleString('nl-NL', { minimumFractionDigits: heel ? 0 : 2, maximumFractionDigits: heel ? 0 : 2 });
        },

        // Shopify-optienamen zijn rommelig ("Color", "vaianten"): toon er een gewone naam voor.
        optieNaam(naam) {
            const laag = String(naam || '').toLowerCase();

            if (/colou?r|kleur/.test(laag)) return 'Kleur';
            if (/kussen/.test(laag)) return 'Kussens';
            if (/maat|size|afmeting/.test(laag)) return 'Maat';

            return 'Uitvoering';
        },

        optionChange(index, value) {
            this.selected[index] = value;
            this.added = false;
        },

        getMarketData(variant) {
            if (! this.market || !variant.market_data) return null;
            return variant.market_data[this.market] ?? null;
        },

        getVariantPrice(variant) {
            return this.getMarketData(variant)?.price ?? variant.price;
        },

        outOfStock(variant) {
            const marketData = this.getMarketData(variant);
            const qty = marketData ? marketData.inventory_quantity : variant.inventory_quantity;
            return variant.inventory_policy && (variant.inventory_policy.toLowerCase() == 'deny') && (qty <= 0);
        },

        variantExistsAndIsInStock() {
            let filteredVariants = variants;

            for (const [key, value] of Object.entries(options)) {
                filteredVariants = filteredVariants.filter((variant) => {
                    return variant[key] == this.selected[key] ?? false;
                });
            }

            if (filteredVariants.length == 1) {
                this.variantEl.value = filteredVariants[0].slug;
                this.selectedVariant = filteredVariants[0];

                this.selectedVariant.out_of_stock = this.outOfStock(this.selectedVariant);

                return ! this.selectedVariant.out_of_stock;
            }

            if (this.selectedVariant) {
                this.selectedVariant.out_of_stock = this.outOfStock(this.selectedVariant);
            }

            return this.selectedVariant && ! this.selectedVariant.out_of_stock;
        },
    }));
};

export {
    createData,
    createStore,
};
